import React, { useState } from "react";
import { useFormikContext } from "formik";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

import FieldsIdentifiers from "../../views/capture/FieldsIdentifiers";
import ActivityLog from "../../views/asset/ActivityLog";
import { SustainabilitySheetProvider } from "../../views/capture/SustainabilitySheetContext";
import useCaptureFields from "../../hooks/capture/useCaptureFields";
import PackingField from "./form/PackingField";
import useAuth from "../../hooks/useAuth";
import useAdmin from "../../hooks/useAdmin";
import AssetReplayLogs from "../../views/asset/AssetReplayLogs";
import { useProject } from "../../contexts/project";
import useEnabledFields from "./hooks/useEnabledFields";
import FieldsReorderModal from "./FieldsReorderModal";
import FieldsSection from "./FieldsSection";
import useQueryParams from "../../hooks/useQueryParams";
import ManageConfigurableFieldsDialog from "./ManageConfigurableFieldsDialog";
import { useLocation, useNavigate } from "react-router";

export default function FormFields({
  clientId,
  type,
  disabledForm,
  displayClient,
  displayProject,
  displayWarehouse,
  colorsOptions = [],
  manufacturersOptions = [],
  dispositionOptions = [],
  asset,
  updateAsset,
  createImage,
  setErrors,
  defaultTab = "replenish_inventory",
  overrideDisplayFields,
  fullWidth,
}) {
  const { user } = useAuth();
  const isAdmin = useAdmin();
  const { settings } = user.company;
  const params = useQueryParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isReceivingWizardDisabled = params.get("disable_receiving_wizard") === "true";
  const currentProject = useProject();
  const [displayFields, loading] = useCaptureFields(currentProject, asset.project.id);
  const display = overrideDisplayFields ?? displayFields;
  const { values, setFieldValue } = useFormikContext();
  const [reorderModalSelectedSectionId, setReorderModalSelectedSectionId] = useState(null);
  const [manageConfigurableFieldsOpen, setManageConfigurableFieldsOpen] = useState(false);

  const handleUpdateAssetField = field => (value, { onSuccess, onFailure } = {}) => {
    const newValue = field === "barcode" && values.barcode ? `${values.barcode}, ${value}` : value;

    if (["barcode", "serialNumber"].includes(field)) {
      setFieldValue(field, newValue);
    }

    updateAsset({
      variables: { id: asset.id, input: { [field]: newValue } },
      onSuccess,
      onFailure: errors => {
        setErrors(errors);
        onFailure && onFailure(errors);
      },
    });
  };

  const isShopperOrGuest = user?.role === "shopper" || user?.role === "guest";
  const { orderedSection: enabledFields } = useEnabledFields({
    display,
    type,
    asset,
    // onRestockInventory,
    // availableQuantityComponent,
    // sameLocation,
    manufacturersOptions,
    colorsOptions,
    dispositionOptions,
    handleUpdateAssetField,
    project: currentProject,
    disabledForm,
    isReceivingWizardDisabled,
  });
  const configurableFields = Object.values(enabledFields)
    .flat()
    .map(({ params }) => params?.field)
    .filter(Boolean);
  const canManageConfigurableFields = isAdmin && configurableFields.length > 0;
  const openFieldOrder = sectionId => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("field_order", "true");
    nextParams.set("field_order_section", sectionId);
    navigate({ pathname: location.pathname, search: nextParams.toString() });
  };
  const closeFieldOrder = () => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("field_order");
    nextParams.delete("field_order_section");
    setReorderModalSelectedSectionId(null);
    navigate({ pathname: location.pathname, search: nextParams.toString() }, { replace: true });
  };
  const fieldOrderSectionId =
    params.get("field_order_section") || reorderModalSelectedSectionId || "identifiers";
  const fieldOrderOpen = params.get("field_order") === "true";

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" width="100%" p={3}>
        <CircularProgress size={44} />
      </Box>
    );
  }

  return (
    <SustainabilitySheetProvider>
      <Box display="flex" flexDirection="column" gap={2}>
        <FieldsIdentifiers
          fullWidth={fullWidth}
          displayClient={displayClient}
          displayProject={displayProject}
          displayWarehouse={displayWarehouse}
          clientId={clientId}
          display={display}
          type={type}
          disabledForm={disabledForm}
          asset={asset}
          createImage={createImage}
          handleUpdateAssetField={handleUpdateAssetField}
          fields={enabledFields.identifiers || []}
          onReorder={!disabledForm ? () => {
            openFieldOrder("identifiers");
          } : undefined}
        />
        <FieldsSection
          fullWidth={fullWidth}
          title="Descriptive"
          fields={enabledFields.descriptive || []}
          disabledForm={disabledForm}
        />
        <FieldsSection
          fullWidth={fullWidth}
          title="Origin"
          fields={enabledFields.origin || []}
          disabledForm={disabledForm}
        />
        <FieldsSection
          fullWidth={fullWidth}
          title="Destination"
          fields={enabledFields.destination || []}
          disabledForm={disabledForm}
        />
        <FieldsSection
          fullWidth={fullWidth}
          title="Configurable Fields"
          fields={enabledFields.configurable || []}
          disabledForm={disabledForm}
          onTitleClick={
            canManageConfigurableFields
              ? () => setManageConfigurableFieldsOpen(true)
              : undefined
          }
        />
        {!isReceivingWizardDisabled && (
          <FieldsSection
            fullWidth={fullWidth}
            title="Storage"
            fields={enabledFields.storage || []}
            disabledForm={disabledForm}
          />
        )}
        {isShopperOrGuest ? (
          settings.reservations && (
            <Card style={{ overflow: "visible" }}>
              <CardContent>
                <AssetReplayLogs
                  assetId={asset.id}
                  defaultSelected="reserved_inventory"
                  displayFilter={false}
                />
              </CardContent>
            </Card>
          )
        ) : (
          <Card style={{ overflow: "visible" }}>
            <CardContent>
              <ActivityLog assetId={asset.id} defaultTab={defaultTab} />
            </CardContent>
          </Card>
        )}
        {display.packing && (
          <Box>
            <Card>
              <CardContent>
                <PackingField />
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
      {fieldOrderOpen && (
        <FieldsReorderModal
          open={fieldOrderOpen}
          fields={enabledFields}
          disabledForm={disabledForm}
          selectedSectionId={fieldOrderSectionId}
          onClose={closeFieldOrder}
        />
      )}
      {manageConfigurableFieldsOpen && (
        <ManageConfigurableFieldsDialog
          fields={configurableFields}
          onClose={() => setManageConfigurableFieldsOpen(false)}
        />
      )}
    </SustainabilitySheetProvider>
  );
}
